import { useCallback, useEffect, useState } from 'react';

const DISPLAY_MODE_QUERY = '(display-mode: standalone)';

function isInstalledDisplayMode() {
  if (typeof window === 'undefined') return false;

  return window.matchMedia?.(DISPLAY_MODE_QUERY).matches
    || window.navigator.standalone === true
    || document.referrer.startsWith('android-app://');
}

function manualInstallMessage() {
  const userAgent = window.navigator.userAgent || '';
  const isIos = /iphone|ipad|ipod/i.test(userAgent);

  if (isIos) {
    return 'Tap Share, then Add to Home Screen. Open Xap Xam from its new icon.';
  }

  return 'Open the browser menu, choose Install app or Add to Home screen, then open Xap Xam from its new icon.';
}

export default function usePwaInstall() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isInstalledDisplayMode);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const displayMode = window.matchMedia?.(DISPLAY_MODE_QUERY);
    const refreshInstalledState = () => setIsInstalled(isInstalledDisplayMode());
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setMessage('');
    };
    const handleInstalled = () => {
      setInstallPrompt(null);
      setIsInstalled(true);
      setMessage('Xap Xam installed. Open it from your home-screen icon.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    if (displayMode?.addEventListener) {
      displayMode.addEventListener('change', refreshInstalledState);
    } else {
      displayMode?.addListener?.(refreshInstalledState);
    }

    refreshInstalledState();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      if (displayMode?.removeEventListener) {
        displayMode.removeEventListener('change', refreshInstalledState);
      } else {
        displayMode?.removeListener?.(refreshInstalledState);
      }
    };
  }, []);

  const install = useCallback(async () => {
    if (isInstalled) {
      setMessage('Xap Xam is already running as an installed app.');
      return 'installed';
    }

    if (!installPrompt) {
      setMessage(manualInstallMessage());
      return 'manual';
    }

    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);

      if (choice?.outcome === 'accepted') {
        setMessage('Installation accepted. Open Xap Xam from its new home-screen icon.');
        return 'accepted';
      }

      setMessage('Installation canceled. You can install Xap Xam whenever you are ready.');
      return 'dismissed';
    } catch (error) {
      setInstallPrompt(null);
      setMessage(manualInstallMessage());
      return 'manual';
    }
  }, [installPrompt, isInstalled]);

  return {
    canInstall: !!installPrompt,
    isInstalled,
    install,
    message,
    clearMessage: () => setMessage(''),
  };
}
